import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  CheckCircle2,
  Globe2,
  Loader2,
  Play,
  RefreshCw,
  SearchCheck,
  TriangleAlert,
} from 'lucide-react';
import {
  getEventExtractionStatus,
  getProcessingRuns,
  getWebIqDiscoveryStatus,
  startQueueBasedProcessing,
  startWebIqDiscovery,
  type EventExtractionResponse,
  type ProcessingRun,
  type WebIqDiscoveryStatus,
} from '../../../services/adminApi';
import type {
  CatalogEntity,
  EntityEventSourceMapping,
} from '../../../types/catalog';
import {
  PrimaryButton,
  SecondaryButton,
} from '../AdminUI';
import {
  Badge,
  ResourceContent,
  ResourceDescription,
  ResourceList,
  ResourceMeta,
  ResourceRow,
  ResourceTitle,
  errorMessage,
} from './CatalogUI';
import {
  borderRadius,
  breakpoints,
  colors,
  spacing,
  typography,
} from '../../../styles/theme';

const TERMINAL_DISCOVERY_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
]);

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
`;

const WorkflowBand = styled.section`
  margin-top: ${spacing[4]};
  border-top: 1px solid ${colors.border.normal};
  border-bottom: 1px solid ${colors.border.normal};
  background: ${colors.background.secondary};
`;

const WorkflowHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  padding: ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};

  h4 {
    margin: 0;
    font-size: ${typography.fontSizes.base};
  }

  p {
    margin: ${spacing[1]} 0 0;
    color: ${colors.text.secondary};
    font-size: ${typography.fontSizes.sm};
    line-height: 1.45;
  }
`;

const WorkflowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const WorkflowStep = styled.div<{ $complete?: boolean }>`
  min-width: 0;
  padding: ${spacing[4]};
  border-right: 1px solid ${colors.border.light};
  background: ${(props) => props.$complete ? colors.success[50] : 'transparent'};

  &:last-child {
    border-right: 0;
  }

  @media (max-width: ${breakpoints.md}) {
    border-right: 0;
    border-bottom: 1px solid ${colors.border.light};

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const StepHeading = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[3]};
`;

const StepNumber = styled.span<{ $complete?: boolean }>`
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.full};
  background: ${(props) => props.$complete ? colors.success[600] : colors.primary[600]};
  color: ${colors.white};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.bold};
`;

const StepTitle = styled.h5`
  margin: 0;
  font-size: ${typography.fontSizes.base};
`;

const StepDescription = styled.p`
  margin: ${spacing[1]} 0 0;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  line-height: 1.5;
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: end;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[3]};
  flex-wrap: wrap;
`;

const CompactField = styled.label`
  display: grid;
  gap: ${spacing[1]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};

  input {
    width: 96px;
    height: 34px;
    padding: 0 ${spacing[2]};
    border: 1px solid ${colors.border.normal};
    border-radius: ${borderRadius.md};
  }
`;

const StatusLine = styled.div<{ $tone?: 'neutral' | 'success' | 'warning' | 'error' }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  min-height: 34px;
  margin-top: ${spacing[3]};
  padding: ${spacing[2]} ${spacing[3]};
  border-left: 3px solid ${(props) => {
    if (props.$tone === 'success') return colors.success[500];
    if (props.$tone === 'warning') return colors.warning[500];
    if (props.$tone === 'error') return colors.error[500];
    return colors.primary[400];
  }};
  background: ${colors.white};
  color: ${(props) => {
    if (props.$tone === 'error') return colors.error[800];
    if (props.$tone === 'warning') return colors.warning[800];
    return colors.text.primary;
  }};
  font-size: ${typography.fontSizes.sm};
  line-height: 1.45;
`;

const StatusDetails = styled.div`
  margin-top: ${spacing[3]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  line-height: 1.5;
`;

const WorkflowActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

interface RelationshipResult {
  mappings: EntityEventSourceMapping[];
}

interface EntityWorkflowProps {
  entity: CatalogEntity;
  mappings: EntityEventSourceMapping[];
  eventCount: number;
  onRefresh: () => Promise<RelationshipResult | undefined>;
}

interface QueueSummary {
  status: 'enqueueing' | 'processing' | 'completed' | 'failed';
  enqueued: number;
  errors: number;
  batches: number;
  processId?: string;
  message?: string;
  startedAt?: string;
  expectedSourceIds: number[];
  runs: ProcessingRun[];
  monitorError?: string;
  gateEnabled?: boolean;
}

type RunOutcome = 'running' | 'success' | 'no-events' | 'partial' | 'failed';

function runOutcome(run: ProcessingRun): RunOutcome {
  if (!run.endTime) return 'running';
  const status = String(run.status || '').toLowerCase();
  if (status === 'completed_with_errors' || (run.totalErrors || 0) > 0) return 'partial';
  if (status === 'success') return 'success';
  if (status.includes('no recent events')) return 'no-events';
  return 'failed';
}

function outcomeLabel(outcome: RunOutcome): string {
  if (outcome === 'success') return 'Succeeded';
  if (outcome === 'no-events') return 'No recent events';
  if (outcome === 'partial') return 'Completed with errors';
  if (outcome === 'failed') return 'Failed';
  return 'Running';
}

function discoveryLabel(status: WebIqDiscoveryStatus): string {
  if (status.status === 'queued') return 'Discovery is queued.';
  if (status.status === 'dispatching') return 'Preparing the entity for discovery.';
  if (status.runs.searching > 0) return 'WebIQ is searching and validating candidate websites.';
  if (status.runs.sourcesActivated > 0) return 'Accepted websites are being activated.';
  if (status.status === 'completed_with_errors') return 'Discovery completed with errors.';
  if (status.status === 'failed') return 'Discovery failed.';
  if (status.status === 'completed') {
    return status.runs.acceptedSources > 0
      ? `Discovery completed with ${status.runs.acceptedSources} accepted website${status.runs.acceptedSources === 1 ? '' : 's'}.`
      : 'Discovery completed without finding a reusable event listing.';
  }
  return 'Discovery workers are running.';
}

export const EntityWorkflow: React.FC<EntityWorkflowProps> = ({
  entity,
  mappings,
  eventCount,
  onRefresh,
}) => {
  const storageKey = `admin:entity-discovery:${entity.entityId}`;
  const queueStorageKey = `admin:entity-processing:${entity.entityId}`;
  const [maxResults, setMaxResults] = useState(10);
  const [region, setRegion] = useState('US');
  const [processId, setProcessId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });
  const [discovery, setDiscovery] = useState<WebIqDiscoveryStatus | null>(null);
  const [discoveryStarting, setDiscoveryStarting] = useState(false);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [baselineMappings, setBaselineMappings] = useState<EntityEventSourceMapping[]>([]);
  const [queueSummary, setQueueSummary] = useState<QueueSummary | null>(() => {
    try {
      const stored = sessionStorage.getItem(queueStorageKey);
      if (!stored) return null;
      const parsed = JSON.parse(stored) as QueueSummary;
      return Array.isArray(parsed.expectedSourceIds) && Array.isArray(parsed.runs)
        ? parsed
        : null;
    } catch {
      return null;
    }
  });
  const [queueRunning, setQueueRunning] = useState(false);
  const refreshRef = useRef(onRefresh);
  const refreshedProcessRef = useRef<string | null>(null);
  const expectedSourceIdsKey = queueSummary?.expectedSourceIds.join(',') || '';

  useEffect(() => {
    refreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    try {
      if (queueSummary) sessionStorage.setItem(queueStorageKey, JSON.stringify(queueSummary));
      else sessionStorage.removeItem(queueStorageKey);
    } catch {
      // Session persistence is optional.
    }
  }, [queueStorageKey, queueSummary]);

  useEffect(() => {
    if (!processId) return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const status = await getWebIqDiscoveryStatus(processId);
        if (cancelled) return;
        setDiscovery(status);
        setDiscoveryError(null);

        if (TERMINAL_DISCOVERY_STATUSES.has(status.status)) {
          if (refreshedProcessRef.current !== processId) {
            refreshedProcessRef.current = processId;
            await refreshRef.current();
          }
          return;
        }
        timer = window.setTimeout(poll, 3000);
      } catch (pollError) {
        if (cancelled) return;
        setDiscoveryError(errorMessage(pollError));
        timer = window.setTimeout(poll, 5000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [processId]);

  useEffect(() => {
    if (queueSummary?.status !== 'processing' || !queueSummary.startedAt) return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        if (queueSummary.gateEnabled && queueSummary.processId) {
          // Gated runs may legitimately skip some active source IDs, so waiting
          // for one WorkflowMetadata row per mapping would never finish. Poll
          // the durable gate batch instead; it accounts for skips and AI runs.
          const gate = await getEventExtractionStatus(queueSummary.processId);
          if (cancelled) return;
          const terminal = ['completed', 'completed_with_errors', 'failed']
            .includes(String(gate.status || '').toLowerCase());
          const failed = gate.status === 'failed' || (gate.runs?.failed || 0) > 0;
          setQueueSummary(current => current ? {
            ...current,
            status: terminal ? (failed ? 'failed' : 'completed') : 'processing',
            enqueued: gate.enqueuedCount,
            errors: gate.runs?.failed ?? gate.errorCount,
            message: gate.status,
            monitorError: undefined,
          } : current);
          if (terminal) {
            await refreshRef.current();
            return;
          }
          timer = window.setTimeout(poll, 3000);
          return;
        }

        // Legacy mode still emits one WorkflowMetadata row per queued source,
        // so retain the original source-by-source monitoring path.
        const response = await getProcessingRuns({
          entityId: entity.entityId,
          startTime: queueSummary.startedAt,
          page: 1,
          pageSize: 200,
        });
        if (cancelled) return;

        const expectedSourceIds = expectedSourceIdsKey
          .split(',')
          .filter(Boolean)
          .map(Number);
        const expectedIds = new Set(expectedSourceIds);
        const latestBySource = new Map<number, ProcessingRun>();
        for (const run of response.runs) {
          if (expectedIds.has(run.eventSourceId) && !latestBySource.has(run.eventSourceId)) {
            latestBySource.set(run.eventSourceId, run);
          }
        }
        const runs = expectedSourceIds
          .map(sourceId => latestBySource.get(sourceId))
          .filter((run): run is ProcessingRun => Boolean(run));
        const terminalRuns = runs.filter(run => runOutcome(run) !== 'running');
        const failedRuns = terminalRuns.filter(run => ['failed', 'partial'].includes(runOutcome(run)));
        const finished = terminalRuns.length >= expectedSourceIds.length;

        setQueueSummary(current => current ? {
          ...current,
          status: finished ? (failedRuns.length > 0 ? 'failed' : 'completed') : 'processing',
          errors: failedRuns.length,
          runs,
          monitorError: undefined,
        } : current);

        if (finished) {
          await refreshRef.current();
          return;
        }
        timer = window.setTimeout(poll, 3000);
      } catch (pollError) {
        if (cancelled) return;
        setQueueSummary(current => current ? {
          ...current,
          monitorError: errorMessage(pollError),
        } : current);
        timer = window.setTimeout(poll, 5000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [
    entity.entityId,
    expectedSourceIdsKey,
    queueSummary?.gateEnabled,
    queueSummary?.processId,
    queueSummary?.startedAt,
    queueSummary?.status,
  ]);

  const activeMappings = mappings.filter((mapping) => mapping.isActive);
  const activeWebIqMappings = activeMappings.filter((mapping) => mapping.sourceOrigin === 'webiq');
  const discoveryRunning = !!discovery && !TERMINAL_DISCOVERY_STATUSES.has(discovery.status);
  const discoveryComplete = !!discovery && TERMINAL_DISCOVERY_STATUSES.has(discovery.status);
  const discoveryFailure = discovery?.dispatch.error || discovery?.errors[0]?.ErrorMessage;

  const changedWebIqMappings = useMemo(() => {
    if (!discoveryComplete || baselineMappings.length === 0) return [];
    const baseline = new Map(baselineMappings.map((mapping) => [mapping.eventSourceId, mapping]));
    return activeWebIqMappings.filter((mapping) => {
      const previous = baseline.get(mapping.eventSourceId);
      return !previous || !previous.isActive;
    });
  }, [activeWebIqMappings, baselineMappings, discoveryComplete]);

  const startDiscovery = async () => {
    setDiscoveryStarting(true);
    setDiscoveryError(null);
    setDiscovery(null);
    setProcessId(null);
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // Session persistence is optional.
    }
    setBaselineMappings(mappings.map((mapping) => ({ ...mapping })));
    refreshedProcessRef.current = null;
    try {
      const result = await startWebIqDiscovery({
        entityId: entity.entityId,
        entityType: entity.entityType,
        maxResults,
        region,
      });
      setProcessId(result.processId);
      try {
        sessionStorage.setItem(storageKey, result.processId);
      } catch {
        // Session persistence is optional.
      }
    } catch (startError) {
      setDiscoveryError(errorMessage(startError));
    } finally {
      setDiscoveryStarting(false);
    }
  };

  const queueEntity = async () => {
    setQueueRunning(true);
    const startedAt = new Date(Date.now() - 5000).toISOString();
    const expectedSourceIds = activeMappings.map(mapping => mapping.eventSourceId);
    setQueueSummary({
      status: 'enqueueing',
      enqueued: 0,
      errors: 0,
      batches: 0,
      startedAt,
      expectedSourceIds,
      runs: [],
    });
    let offset = 0;
    let enqueued = 0;
    let errors = 0;
    let batches = 0;
    let latest: EventExtractionResponse | null = null;

    try {
      let hasMore = true;
      while (hasMore) {
        latest = await startQueueBasedProcessing({
          entityId: entity.entityId,
          batchSize: 100,
          offset,
          processAllBatches: false,
        });
        enqueued += latest.enqueuedCount ?? 0;
        errors += latest.errorCount ?? 0;
        batches += 1;
        hasMore = Boolean(latest.hasMoreBatches && latest.nextOffset != null);
        offset = latest.nextOffset ?? offset + 100;
        setQueueSummary({
          status: 'enqueueing',
          enqueued,
          errors,
          batches,
          processId: latest.processId,
          startedAt,
          expectedSourceIds,
          runs: [],
        });
      }

      setQueueSummary({
        status: errors > 0 ? 'failed' : 'processing',
        enqueued,
        errors,
        batches,
        processId: latest?.processId,
        gateEnabled: latest?.gateEnabled,
        message: latest?.message,
        startedAt,
        expectedSourceIds,
        runs: [],
      });
    } catch (queueError) {
      setQueueSummary({
        status: 'failed',
        enqueued,
        errors: errors + 1,
        batches,
        message: errorMessage(queueError),
        startedAt,
        expectedSourceIds,
        runs: [],
      });
    } finally {
      setQueueRunning(false);
    }
  };

  return (
    <WorkflowBand aria-label="Entity source discovery and event extraction workflow">
      <WorkflowHeader>
        <SearchCheck size={22} aria-hidden="true" />
        <div>
          <h4>Discover sources and extract events</h4>
          <p>
            Run these steps for {entity.entityName}. Review activated websites after discovery,
            then re-process every active source for this entity.
          </p>
        </div>
      </WorkflowHeader>

      <WorkflowGrid>
        <WorkflowStep $complete={discoveryComplete && discovery?.status !== 'failed'}>
          <StepHeading>
            <StepNumber $complete={discoveryComplete && discovery?.status !== 'failed'}>
              {discoveryComplete && discovery?.status !== 'failed' ? <CheckCircle2 size={17} /> : '1'}
            </StepNumber>
            <div>
              <StepTitle>Find relevant websites with WebIQ</StepTitle>
              <StepDescription>
                WebIQ searches, validates reusable event listings, activates accepted sites, and
                automatically queues newly accepted sites once.
              </StepDescription>
            </div>
          </StepHeading>

          <SettingsRow>
            <CompactField>
              Max sites
              <input
                type="number"
                min={1}
                max={50}
                value={maxResults}
                onChange={(event) => setMaxResults(Number(event.target.value))}
                disabled={discoveryStarting || discoveryRunning}
              />
            </CompactField>
            <CompactField>
              Region
              <input
                value={region}
                maxLength={2}
                onChange={(event) => setRegion(event.target.value.toUpperCase())}
                disabled={discoveryStarting || discoveryRunning}
              />
            </CompactField>
            <PrimaryButton
              type="button"
              onClick={startDiscovery}
              disabled={discoveryStarting || discoveryRunning || maxResults < 1 || maxResults > 50 || region.length !== 2}
            >
              {discoveryStarting || discoveryRunning
                ? <Spinner size={16} />
                : <Globe2 size={16} />}
              {discoveryRunning ? 'Discovering websites' : discoveryComplete ? 'Run WebIQ again' : 'Run WebIQ'}
            </PrimaryButton>
          </SettingsRow>

          {discovery && (
            <StatusLine $tone={
              discovery.status === 'failed'
                ? 'error'
                : discovery.status === 'completed_with_errors'
                  ? 'warning'
                  : discoveryComplete
                    ? 'success'
                    : 'neutral'
            }>
              {discovery.status === 'failed'
                ? <TriangleAlert size={17} />
                : discoveryComplete
                  ? <CheckCircle2 size={17} />
                  : <Spinner size={17} />}
              <div>
                <strong>{discoveryLabel(discovery)}</strong>
                {!discoveryComplete && (
                  <div>
                    {discovery.runs.searching} searching · {discovery.runs.completed} completed ·{' '}
                    {discovery.runs.acceptedSources} accepted
                  </div>
                )}
              </div>
            </StatusLine>
          )}
          {discoveryError && (
            <StatusLine $tone="error">
              <TriangleAlert size={17} /> {discoveryError}
            </StatusLine>
          )}

          {discovery && (discoveryComplete || discoveryFailure) && (
            <StatusDetails>
              Process {discovery.processId}
              {discoveryFailure ? ` · ${discoveryFailure}` : ''}
            </StatusDetails>
          )}

          {discoveryComplete && discovery?.status !== 'failed' && (
            <StatusDetails>
              {changedWebIqMappings.length > 0
                ? `${changedWebIqMappings.length} newly activated WebIQ source${changedWebIqMappings.length === 1 ? '' : 's'}:`
                : `${activeWebIqMappings.length} active WebIQ source${activeWebIqMappings.length === 1 ? '' : 's'} now linked. Review the full source list below.`}
              {changedWebIqMappings.length > 0 && (
                <ResourceList>
                  {changedWebIqMappings.map((mapping) => (
                    <ResourceRow key={mapping.eventSourceId}>
                      <ResourceContent>
                        <ResourceTitle>{mapping.source}</ResourceTitle>
                        <ResourceMeta>
                          <Badge $tone="success">Newly active</Badge>
                          <span>Source #{mapping.eventSourceId}</span>
                        </ResourceMeta>
                      </ResourceContent>
                    </ResourceRow>
                  ))}
                </ResourceList>
              )}
            </StatusDetails>
          )}
        </WorkflowStep>

        <WorkflowStep $complete={queueSummary?.status === 'completed'}>
          <StepHeading>
            <StepNumber $complete={queueSummary?.status === 'completed'}>
              {queueSummary?.status === 'completed' ? <CheckCircle2 size={17} /> : '2'}
            </StepNumber>
            <div>
              <StepTitle>Process this entity's active sources</StepTitle>
              <StepDescription>
                Enqueue all {activeMappings.length} active source{activeMappings.length === 1 ? '' : 's'}
                {' '}on partner-processing. Use this after review to recrawl manual and WebIQ sites together.
              </StepDescription>
            </div>
          </StepHeading>

          <WorkflowActions>
            <PrimaryButton
              type="button"
              onClick={queueEntity}
              disabled={
                queueRunning ||
                queueSummary?.status === 'processing' ||
                discoveryStarting ||
                discoveryRunning ||
                activeMappings.length === 0
              }
            >
              {queueRunning || queueSummary?.status === 'processing' ? <Spinner size={16} /> : <Play size={16} />}
              {queueSummary?.status === 'processing'
                ? `Processing ${queueSummary.runs.filter(run => runOutcome(run) !== 'running').length}/${queueSummary.expectedSourceIds.length}`
                : queueRunning
                ? `Queued ${queueSummary?.enqueued ?? 0}`
                : `Queue ${activeMappings.length} active source${activeMappings.length === 1 ? '' : 's'}`}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => onRefresh()} disabled={queueRunning}>
              <RefreshCw size={16} /> Refresh Event Info ({eventCount})
            </SecondaryButton>
          </WorkflowActions>

          {discoveryRunning && (
            <StatusDetails>Finish source discovery before queueing a full entity recrawl.</StatusDetails>
          )}
          {activeMappings.length === 0 && (
            <StatusDetails>No active sources are available yet. Run WebIQ or link a source first.</StatusDetails>
          )}

          {queueSummary && (
            <StatusLine $tone={queueSummary.status === 'failed' ? 'error' : queueSummary.status === 'completed' ? 'success' : 'neutral'}>
              {queueSummary.status === 'enqueueing' || queueSummary.status === 'processing'
                ? <Spinner size={17} />
                : queueSummary.status === 'completed'
                  ? <CheckCircle2 size={17} />
                  : <TriangleAlert size={17} />}
              <div>
                <strong>
                  {queueSummary.status === 'enqueueing'
                    ? `Enqueueing active sources: ${queueSummary.enqueued} queued`
                    : queueSummary.status === 'processing'
                      ? `${queueSummary.runs.filter(run => runOutcome(run) !== 'running').length} of ${queueSummary.expectedSourceIds.length} sources finished`
                    : queueSummary.status === 'completed'
                      ? `Extraction completed for ${queueSummary.enqueued} source${queueSummary.enqueued === 1 ? '' : 's'}`
                      : 'Extraction completed with errors'}
                </strong>
                <div>
                  {queueSummary.batches} batch{queueSummary.batches === 1 ? '' : 'es'} ·{' '}
                  {queueSummary.errors} source errors ·{' '}
                  {queueSummary.runs.reduce((total, run) => total + (run.totalNewEvents || 0), 0)} new events
                  {queueSummary.processId ? ` · Process ${queueSummary.processId}` : ''}
                </div>
              </div>
            </StatusLine>
          )}

          {queueSummary?.monitorError && (
            <StatusDetails>Could not refresh extraction status: {queueSummary.monitorError}</StatusDetails>
          )}

          {queueSummary && queueSummary.runs.length > 0 && (
            <ResourceList>
              {queueSummary.runs.map(run => {
                const outcome = runOutcome(run);
                return (
                  <ResourceRow key={run.id}>
                    <ResourceContent>
                      <ResourceTitle>{run.source || `Source #${run.eventSourceId}`}</ResourceTitle>
                      {(outcome === 'failed' || outcome === 'partial') && (
                        <ResourceDescription>
                          {outcome === 'partial'
                            ? `${run.totalErrors || 0} event processing error${run.totalErrors === 1 ? '' : 's'}`
                            : run.status}
                        </ResourceDescription>
                      )}
                      <ResourceMeta>
                        <Badge $tone={
                          outcome === 'success'
                            ? 'success'
                            : outcome === 'failed' || outcome === 'partial'
                              ? 'warning'
                              : outcome === 'running'
                                ? 'info'
                                : 'neutral'
                        }>
                          {outcomeLabel(outcome)}
                        </Badge>
                        <span>{run.stage || 'Waiting for worker'}</span>
                        <span>{run.totalEventsFound || 0} found</span>
                        <span>{run.totalNewEvents || 0} new</span>
                      </ResourceMeta>
                    </ResourceContent>
                  </ResourceRow>
                );
              })}
            </ResourceList>
          )}

          {queueSummary?.status === 'processing' && (
            <StatusDetails>
              Queue workers are scraping and extracting events. This status updates automatically.
            </StatusDetails>
          )}

          {(queueSummary?.status === 'completed' || queueSummary?.status === 'failed') && (
            <StatusDetails>
              Event Info was refreshed after all source runs completed. Failed rows above include the
              extraction stage and stored error.
            </StatusDetails>
          )}
        </WorkflowStep>
      </WorkflowGrid>
    </WorkflowBand>
  );
};
